import React, { useEffect, useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslation } from "react-i18next";
import { CalendarDays, Palette, Snowflake, Star, Leaf, Flower2, Sun, Heart, Moon, PartyPopper, Ban, Play, Square, Music } from "lucide-react";
import ColorOverridePicker from "@/components/ColorOverridePicker";
import DoorImageButton from "@/components/DoorImageButton";
import { AdventAmbience } from "@/components/AdventAmbience";
import { ADVENT_THEME_KEYS, adventBackground, adventSwatch, adventTheme } from "@/lib/adventThemes";
import { startAmbient, stopAmbient, playDoorOpen, isAmbientPlaying } from "@/lib/adventMusic";

const THEME_ICONS = {
  snow: Snowflake,
  stars: Star,
  autumn: Leaf,
  spring: Flower2,
  summer: Sun,
  valentine: Heart,
  ramadan: Moon,
  birthday: PartyPopper,
  none: Ban,
};

const AmbiencePreview = ({ game }) => (
  <div
    className="relative overflow-hidden rounded-2xl border border-white/10 p-3 h-[168px]"
    style={{ background: adventBackground(game) }}
    data-testid="advent-ambience-preview"
  >
    <AdventAmbience game={game} className="rounded-2xl" />
    <div className="relative grid grid-cols-6 gap-1.5">
      {Array.from({ length: 12 }, (_, i) => (
        <div
          key={i}
          className={`aspect-square rounded-lg border flex items-center justify-center text-[11px] font-black ${
            i < 3 ? "border-amber-300/60 bg-white/[0.08] text-amber-200" : "border-white/10 bg-white/[0.03] text-white/35"
          }`}
          style={{ fontFamily: "Georgia, serif" }}
        >
          {i + 1}
        </div>
      ))}
    </div>
  </div>
);

function resizeDoors(doors, count) {
  const next = [];
  for (let i = 0; i < count; i++) {
    next.push(
      doors[i] || {
        day: i + 1,
        label: "",
        win_probability: 100,
        initial_quantity: null,
        remaining_quantity: null,
        claim_days: 14,
      }
    );
    next[i] = { ...next[i], day: i + 1 };
  }
  return next;
}

export const AdventEditor = ({ game, onGameChange }) => {
  const { t } = useTranslation();
  const [previewing, setPreviewing] = useState(false);
  // Stop the music preview when leaving the editor or switching theme.
  useEffect(() => () => stopAmbient(), []);
  useEffect(() => {
    if (previewing) {
      stopAmbient();
      setPreviewing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.advent_theme]);
  const days = parseInt(game.advent_days || 24, 10);
  const doors = resizeDoors(game.advent_doors || [], days);
  const mode = game.advent_mode || "daily";

  function patchDoor(i, partial) {
    const next = doors.map((d, idx) => (idx === i ? { ...d, ...partial } : d));
    onGameChange({ advent_doors: next });
  }

  return (
    <div className="qrise-card p-6" data-testid="advent-editor">
      <div className="flex items-center gap-2 mb-4">
        <CalendarDays className="h-4 w-4 text-primary" />
        <div className="text-sm font-semibold">
          {t("game_editor.advent_title", "Calendrier de l'Avent — Configuration")}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label className="text-xs font-medium">
            {t("game_editor.advent_mode", "Mode de jeu")}
          </Label>
          <Select
            value={mode}
            onValueChange={(v) => onGameChange({ advent_mode: v })}
          >
            <SelectTrigger className="mt-1.5 rounded-xl h-10" data-testid="advent-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">
                {t("game_editor.advent_mode_daily", "Quotidien — une case par jour")}
              </SelectItem>
              <SelectItem value="free">
                {t("game_editor.advent_mode_free", "Libre — une seule case au choix")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-medium">
            {t("game_editor.advent_days", "Nombre de cases")}
          </Label>
          <Select
            value={String(days)}
            onValueChange={(v) => {
              const n = parseInt(v, 10);
              onGameChange({ advent_days: n, advent_doors: resizeDoors(doors, n) });
            }}
          >
            <SelectTrigger className="mt-1.5 rounded-xl h-10" data-testid="advent-days-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7</SelectItem>
              <SelectItem value="12">12</SelectItem>
              <SelectItem value="24">24</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {mode === "daily" && (
          <div>
            <Label className="text-xs font-medium">
              {t("game_editor.advent_start", "Date de la case n°1")}
            </Label>
            <Input
              type="date"
              value={(game.advent_start_date || "").slice(0, 10)}
              onChange={(e) => onGameChange({ advent_start_date: e.target.value || null })}
              className="mt-1.5 rounded-xl h-10"
              data-testid="advent-start-date-input"
            />
          </div>
        )}
      </div>

      {mode === "daily" && (
        <label className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-border/60 px-3 py-2.5 cursor-pointer">
          <div>
            <div className="text-xs font-semibold">
              {t("game_editor.advent_catchup", "Rattrapage des cases manquées")}
            </div>
            <div className="text-[11px] text-muted-foreground">
              {t(
                "game_editor.advent_catchup_hint",
                "Si activé, un joueur peut ouvrir les cases des jours passés. Sinon, une case manquée est perdue."
              )}
            </div>
          </div>
          <Switch
            checked={!!game.advent_catchup}
            onCheckedChange={(v) => onGameChange({ advent_catchup: v })}
            data-testid="advent-catchup-switch"
          />
        </label>
      )}

      {mode === "daily" && (
        <label className="mt-2.5 flex items-center justify-between gap-3 rounded-xl border border-violet-500/25 bg-violet-500/5 px-3 py-2.5 cursor-pointer">
          <div>
            <div className="text-xs font-semibold">
              {t("game_editor.advent_reminder", "Rappel quotidien par email")}
            </div>
            <div className="text-[11px] text-muted-foreground">
              {t(
                "game_editor.advent_reminder_hint",
                "Chaque matin (8h, heure de La Réunion), les participants qui n'ont pas encore ouvert la case du jour reçoivent « Votre case du jour vous attend 🎁 »."
              )}
            </div>
          </div>
          <Switch
            checked={!!game.advent_reminder_enabled}
            onCheckedChange={(v) => onGameChange({ advent_reminder_enabled: v })}
            data-testid="advent-reminder-switch"
          />
        </label>
      )}

      <div className="mt-6 rounded-2xl border border-border/60 p-4" data-testid="advent-ambience-section">
        <div className="flex items-center gap-2 mb-3">
          <Palette className="h-4 w-4 text-primary" />
          <div className="text-xs font-semibold">
            {t("game_editor.advent_ambience_title", "Ambiance & décor")}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2" role="radiogroup">
          {ADVENT_THEME_KEYS.map((key) => {
            const Icon = THEME_ICONS[key];
            const active = adventTheme(game) === key;
            return (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => onGameChange({ advent_theme: key })}
                className={`group rounded-xl border p-1.5 text-left transition-colors ${
                  active ? "border-primary ring-2 ring-primary/30" : "border-border/60 hover:border-foreground/30"
                }`}
                data-testid={`advent-theme-${key}`}
              >
                <div
                  className="h-10 rounded-lg flex items-center justify-center text-white/90"
                  style={{ background: adventSwatch(key, game) }}
                >
                  <Icon className="h-4 w-4" />
                </div>
                <div className="mt-1.5 text-[10.5px] font-semibold leading-tight line-clamp-2">
                  {t(`game_editor.advent_theme_${key}`)}
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-medium">
              {t("game_editor.advent_intensity", "Intensité des particules")}
            </Label>
            <Select
              value={game.advent_particles_intensity || "normal"}
              onValueChange={(v) => onGameChange({ advent_particles_intensity: v })}
              disabled={adventTheme(game) === "none"}
            >
              <SelectTrigger className="mt-1.5 rounded-xl h-10" data-testid="advent-intensity-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="light">{t("game_editor.advent_intensity_light", "Léger")}</SelectItem>
                <SelectItem value="normal">{t("game_editor.advent_intensity_normal", "Normal")}</SelectItem>
                <SelectItem value="festive">{t("game_editor.advent_intensity_festive", "Festif")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <ColorOverridePicker
            label={t("game_editor.advent_bg_color", "Couleur de fond du calendrier")}
            value={game.advent_bg_color}
            onChange={(v) => onGameChange({ advent_bg_color: v })}
            previewText={t("game_editor.advent_bg_preview", "Fond")}
            testIdPrefix="advent-bg-color"
          />
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground leading-snug">
          {t(
            "game_editor.advent_bg_hint",
            "Sans couleur personnalisée, le décor du thème est utilisé (le thème Anniversaire reprend la couleur principale du jeu). Une couleur claire est automatiquement assombrie pour garder le texte lisible."
          )}
        </p>

        <div className="mt-4">
          <Label className="text-xs font-medium flex items-center gap-1.5">
            <Music className="h-3.5 w-3.5" />
            {t("game_editor.advent_sound", "Ambiance sonore")}
          </Label>
          <Select
            value={game.advent_sound || "music_sfx"}
            onValueChange={(v) => onGameChange({ advent_sound: v })}
          >
            <SelectTrigger className="mt-1.5 rounded-xl h-10" data-testid="advent-sound-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="music_sfx">{t("game_editor.advent_sound_music_sfx", "Musique du thème + son d'ouverture")}</SelectItem>
              <SelectItem value="sfx">{t("game_editor.advent_sound_sfx", "Son d'ouverture uniquement")}</SelectItem>
              <SelectItem value="off">{t("game_editor.advent_sound_off", "Silence")}</SelectItem>
            </SelectContent>
          </Select>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-xl h-9 text-xs"
              disabled={adventTheme(game) === "none"}
              onClick={() => {
                if (isAmbientPlaying()) {
                  stopAmbient();
                  setPreviewing(false);
                } else {
                  startAmbient(adventTheme(game));
                  setPreviewing(true);
                }
              }}
              data-testid="advent-music-preview-btn"
            >
              {previewing ? <Square className="h-3.5 w-3.5 mr-1.5" /> : <Play className="h-3.5 w-3.5 mr-1.5" />}
              {previewing
                ? t("game_editor.advent_music_stop", "Arrêter")
                : t("game_editor.advent_music_play", "Écouter")}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-xl h-9 text-xs"
              onClick={() => playDoorOpen(adventTheme(game))}
              data-testid="advent-sfx-preview-btn"
            >
              {t("game_editor.advent_sfx_play", "Son d'ouverture")}
            </Button>
          </div>
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground leading-snug">
          {t(
            "game_editor.advent_sound_hint",
            "Musique et sons sont générés par le navigateur (aucun fichier à charger). Le joueur peut couper le son à tout moment via le haut-parleur affiché sur le calendrier."
          )}
        </p>
        <div className="mt-3">
          <AmbiencePreview game={game} />
        </div>
      </div>

      <div className="mt-5">
        <div className="text-xs font-semibold mb-2">
          {t("game_editor.advent_doors", "Les cases — lot, probabilité de gain et stock")}
        </div>
        <div className="space-y-2 max-h-[460px] overflow-y-auto pr-1">
          {doors.map((d, i) => (
            <div
              key={i}
              className="rounded-xl border border-border/60 p-2.5"
              data-testid={`advent-door-row-${i + 1}`}
            >
              <div className="flex items-center gap-2">
                <div className="h-8 w-9 shrink-0 rounded-lg bg-muted/60 flex items-center justify-center text-xs font-bold">
                  {d.day}
                </div>
                <Input
                  value={d.label || ""}
                  placeholder={t("game_editor.advent_prize_ph", "Ex : Café offert")}
                  onChange={(e) => patchDoor(i, { label: e.target.value })}
                  className="h-8 rounded-lg text-xs min-w-0 flex-1"
                  data-testid={`advent-door-label-${i + 1}`}
                />
                <DoorImageButton
                  image={d.image}
                  index={i}
                  onChange={(url) => patchDoor(i, { image: url })}
                />
              </div>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                <div className="flex items-center gap-1.5">
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={d.win_probability ?? 100}
                    onChange={(e) =>
                      patchDoor(i, {
                        win_probability: e.target.value === "" ? 0 : Math.max(0, Math.min(100, parseFloat(e.target.value))),
                      })
                    }
                    className="h-8 rounded-lg text-xs min-w-0"
                    data-testid={`advent-door-prob-${i + 1}`}
                  />
                  <span className="text-[10px] font-semibold text-muted-foreground whitespace-nowrap">
                    {t("game_editor.advent_col_prob", "% de gain")}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Input
                    type="number"
                    min={0}
                    value={d.initial_quantity ?? ""}
                    placeholder="∞"
                    onChange={(e) => {
                      const v = e.target.value === "" ? null : Math.max(0, parseInt(e.target.value, 10) || 0);
                      patchDoor(i, { initial_quantity: v, remaining_quantity: v });
                    }}
                    className="h-8 rounded-lg text-xs min-w-0"
                    data-testid={`advent-door-stock-${i + 1}`}
                  />
                  <span className="text-[10px] font-semibold text-muted-foreground whitespace-nowrap">
                    {t("game_editor.advent_col_stock", "Stock")}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground leading-snug">
          {t(
            "game_editor.advent_doors_hint",
            "Chaque case a son lot et sa probabilité. Une case sans lot est toujours perdante. Le stock limite le nombre de gains (vide = illimité)."
          )}{" "}
          {t("game_editor.advent_door_image_hint", "L'image (facultative, carrée de préférence) apparaît au dos de la case quand elle s'ouvre.")}
        </p>
      </div>
    </div>
  );
};

export default AdventEditor;
